import cn from 'classnames';
import Heading from '../heading';
import type { VNode, ComponentChildren, ComponentProps } from 'preact';
import type { HTMLAttributes, PickOptional } from '../../types/utility';

type ConditionalChildren =
  | {
      heading: string | PickOptional<ComponentProps<typeof Heading>, 'level'>;
      subheading?: string | string[] | ComponentChildren;
      children: ComponentChildren;
    }
  | {
      heading?: never;
      subheading?: never;
      children: [VNode<typeof Heading>, ...ComponentChildren[]]; // the generic interface of VNode technically doesn't work, so `Heading` isn't actually enforced, but at least two children are
    };
type Props = {
  layout?: 'column' | 'row';
  type?:
    | 'unbounded'
    | 'unbounded-padded'
    | 'bounded'
    | 'bounded-primary'
    | 'bounded-secondary'
    | 'bounded-tertiary';
} & ConditionalChildren;

export default function Section({
  children,
  className,
  heading: passedHeading,
  layout = 'column',
  subheading: passedSubheading,
  type = 'unbounded',
  ...props
}: Props & HTMLAttributes<HTMLElement>) {
  const classList =
    cn(className, {
      [`section-layout-${layout}`]: layout !== 'column' && type !== 'bounded-tertiary',
      [type]: type !== 'unbounded',
    }) || undefined;

  // create heading element
  let heading;
  if (typeof passedHeading === 'string') {
    heading = <h2>{passedHeading}</h2>;
  } else if (passedHeading) {
    heading = <Heading level="h2" {...passedHeading} />;
  }

  // create subheading element(s)
  let subheading;
  if (Array.isArray(passedSubheading)) {
    subheading = passedSubheading.map((subheading) => <p key={subheading}>{subheading}</p>);
  } else if (typeof passedSubheading === 'string') {
    subheading = <p>{passedSubheading}</p>;
  } else if (passedSubheading) {
    subheading = passedSubheading;
  }

  // create hgroup or sole heading
  let hgroup;
  if (heading && subheading) {
    hgroup = (
      <hgroup>
        {heading}
        {subheading}
      </hgroup>
    );
  } else if (heading) {
    hgroup = heading;
  }

  return (
    <section className={classList} {...props}>
      {hgroup}
      {children}
    </section>
  );
}
